import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getOnsiteLiveAdminStatus, getOnsiteLiveLookups } from '@/api/onsiteLiveAdmin';
import { useAuth } from '@/auth/useAuth';
import { ErrorBanner } from '../BranchRelationships/shared';
import { BranchSetsTab, SecurityGroupsTab, SubscriptionGroupsTab } from './ReferenceTabs';
import { UsersTab } from './UsersTab';
import { SubscriptionsTab } from './SubscriptionsTab';

type Tab = 'users' | 'security' | 'subscription' | 'branches' | 'subscriptions';

const tabs: { id: Tab; label: string }[] = [
  { id: 'users', label: 'Users' },
  { id: 'security', label: 'Security groups' },
  { id: 'subscription', label: 'Subscription groups' },
  { id: 'branches', label: 'Branch sets' },
  { id: 'subscriptions', label: 'Subscriptions' },
];

/**
 * Settings › OnsiteLive users — replaces OnBudget's "Onsite Admin" ribbon
 * (plan oscar/docs/OnsiteLiveAdmin-Migration-Plan.md §5). The rows are the
 * oscar.OnsiteLive* tables (migration 141); OnsiteLive, OnBudget and OnFlow
 * read them through same-name views on SQ04.
 *
 * Helpdesk reads. Admin edits, but only after the cutover: until then OnBudget
 * still owns the data and the shadow import would overwrite any change here, so
 * the API refuses writes and this page says so.
 */
export function OnsiteLiveAdminPage() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<Tab>('users');

  const statusQuery = useQuery({ queryKey: ['onsitelive-admin', 'status'], queryFn: ({ signal }) => getOnsiteLiveAdminStatus(signal) });
  const lookupsQuery = useQuery({ queryKey: ['onsitelive-admin', 'lookups'], queryFn: ({ signal }) => getOnsiteLiveLookups(signal) });

  const writesOpen = statusQuery.data?.WritesOpen === true;
  const canWrite = isAdmin && writesOpen;

  return (
    <section className="max-w-7xl">
      <header className="mb-4">
        <h1 className="font-heading text-2xl font-bold text-navy">OnsiteLive users</h1>
        <p className="mt-1 text-sm text-darkGrey">
          Accounts, report security groups, subscription groups, branch sets and report subscriptions for OnsiteLive, OnBudget and OnFlow.
        </p>
        {!isAdmin && <p className="mt-1 text-xs text-darkGrey/70">Read only — changes are made by an Oscar admin.</p>}
      </header>

      {statusQuery.isSuccess && !writesOpen && (
        <div role="status" className="mb-4 rounded border border-orange/40 bg-orange/5 px-3 py-2 text-sm text-darkGrey">
          <span className="font-semibold text-orange">Read only until the cutover. </span>
          {statusQuery.data.Reason}
        </div>
      )}
      {statusQuery.isError && <ErrorBanner message="Could not check whether editing is open, so the page is read only." />}
      {lookupsQuery.isError && <ErrorBanner message={(lookupsQuery.error as Error).message || 'Could not load the groups and branch sets.'} />}

      <div className="inline-flex flex-wrap rounded border border-border bg-white p-0.5 text-sm" role="tablist" aria-label="OnsiteLive users">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={tab === t.id ? 'rounded bg-navy px-3 py-1 font-medium text-white' : 'rounded px-3 py-1 font-medium text-darkGrey/70 hover:text-navy'}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4" role="tabpanel">
        {tab === 'users' && <UsersTab lookups={lookupsQuery.data} canWrite={canWrite} />}
        {tab === 'security' && <SecurityGroupsTab lookups={lookupsQuery.data} canWrite={canWrite} />}
        {tab === 'subscription' && <SubscriptionGroupsTab lookups={lookupsQuery.data} canWrite={canWrite} />}
        {tab === 'branches' && <BranchSetsTab lookups={lookupsQuery.data} canWrite={canWrite} />}
        {/* Subscriptions have their own cutover (plan section 8), so their own read-only switch. */}
        {tab === 'subscriptions' && <SubscriptionsTab />}
      </div>
    </section>
  );
}
